'use client';

// =============================================================================
// ActivityTracker — mounted once in the root layout, inside AuthProvider
// (docs/designs/USER_ACTIVITY_LOGS_AND_ANALYTICS.md §1 "web").
//
// While a session exists it logs one `page.view` per distinct page (pathname +
// section/tab), installs the fetch observer that reports every mutating /api
// call, and flushes the queue when the tab is hidden or unloading. Without a
// session tracking is switched off and nothing is queued.
//
// useSearchParams needs a Suspense boundary in the app router; the wrapper
// component provides it so the root layout can render this statically.
// =============================================================================

import { Suspense, useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuth } from './AuthProvider';
import { pagePathWithContext } from '@/lib/activity/actions';
import {
  getActivityQueue,
  installFetchObserver,
  setActivityEnabled,
  trackEvent
} from '@/lib/activity/client';

function Tracker() {
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';
  // The page the visitor is on, stamped onto observed API events. A ref so the
  // observer callback (installed once) always sees the current page.
  const currentPageRef = useRef<string | null>(null);

  useEffect(() => {
    setActivityEnabled(isAuthenticated);
    if (!isAuthenticated) currentPageRef.current = null;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const uninstall = installFetchObserver(window, event => {
      trackEvent(event.action, {
        ...event,
        page_path: event.page_path ?? currentPageRef.current ?? undefined
      });
    });
    const flush = () => getActivityQueue().flush('pagehide');
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      uninstall();
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !pathname) return;
    const page = pagePathWithContext(pathname, search);
    if (currentPageRef.current === page) return;
    currentPageRef.current = page;
    trackEvent('page.view', { page_path: page });
  }, [isAuthenticated, pathname, search]);

  return null;
}

export default function ActivityTracker() {
  return (
    <Suspense fallback={null}>
      <Tracker />
    </Suspense>
  );
}
